import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { itmId } from "@/lib/display-id";

interface ParentItem {
  id: number;
  name: string;
  brand?: string | null;
}

interface Props {
  items: ParentItem[];
  value: number | null | undefined;
  onChange: (id: number) => void;
}

// 2026-05-21: searchable parent-item picker for the "variant of" flow.
// Replaces a long plain <Select>; filters by ITM code, name, or brand.
export function ParentItemCombobox({ items, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = items.find((it) => it.id === value);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter(
        (it) =>
          it.name.toLowerCase().includes(q) ||
          (it.brand ?? "").toLowerCase().includes(q) ||
          itmId(it.id).toLowerCase().includes(q),
      )
    : items;

  // 2026-05-21: brand intentionally omitted from the label (operator request) — it's now
  // a spec, shown inside the variant form. Still searchable by brand below.
  const label = (it: ParentItem) => `${itmId(it.id)} — ${it.name}`;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", !selected && "text-muted-foreground")}
        >
          <span className="truncate">{selected ? label(selected) : "Select parent item…"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search by name, code, brand…" value={search} onValueChange={setSearch} />
          <CommandList className="max-h-[260px]">
            <CommandEmpty>No item found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((it) => (
                <CommandItem
                  key={it.id}
                  value={String(it.id)}
                  onSelect={() => { onChange(it.id); setSearch(""); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === it.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{label(it)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
